import { FC } from 'react';
import {
  FileInput,
  InfoCard,
  Modal,
  TextInput,
  Typography,
} from 'melp-design/components';
import { useTranslation } from 'react-i18next';
import { ModalProps, useModalContext } from 'store/modal';
import { Controller, SubmitHandler, useForm } from 'react-hook-form-latest';
import { Stack } from '@mui/material';
import { UploadFileOutlined } from '@mui/icons-material';
import { JobStatusModal } from 'components/modals';
import { GenerateTrainingModal } from 'containers/surveys';
import {
  MAX_QUESTIONS_FOR_AI_GENERATION,
  useGenerateTrainingFromImport,
} from 'store/surveys';
import { useJobsContext } from 'store/jobs';
import { useUploadFile } from 'store/files';
import { useTrainingGenerationJobStatusProps } from '../training-generation-progress-modal/use-training-generation-job-status-props';

interface Values {
  name: string;
  fileIds: File[];
}

const INITIAL_VALUES: Values = {
  name: '',
  fileIds: [],
};

interface Props extends ModalProps {}

export const GenerateTrainingFromImportModal: FC<Props> = ({ closeModal }) => {
  const { t } = useTranslation();
  const { openModal } = useModalContext();
  const { addInProgressJob } = useJobsContext();
  const trainingJobStatusProps = useTrainingGenerationJobStatusProps();

  const { mutateAsync: uploadFile, isLoading: isUploading } = useUploadFile();
  const { mutate: generateTrainingFromImport, isLoading: isImporting } =
    useGenerateTrainingFromImport();

  const { control, handleSubmit } = useForm<Values>({
    defaultValues: INITIAL_VALUES,
  });

  const onImport: SubmitHandler<Values> = async (values) => {
    const fileIds = await Promise.all(
      values.fileIds.map(async (file) => {
        const { data } = await uploadFile({ file });
        return data.id;
      }),
    );

    generateTrainingFromImport(
      { fileIds, name: values.name },
      {
        onSuccess: ({ data }) => {
          addInProgressJob(data.jobId);
          closeModal();
          openModal({
            component: JobStatusModal,
            props: trainingJobStatusProps(data.jobId),
            closable: false,
          });
        },
      },
    );
  };

  return (
    <form onSubmit={handleSubmit(onImport)}>
      <Modal.Content
        title={t('components.file_input.generate_ai')}
        actions={[
          {
            variant: 'neutral-outline',
            onClick: () => closeModal(),
            label: t('common.cancel'),
          },
          {
            variant: 'primary',
            label: t('actions.import'),
            type: 'submit',
            disabled: isUploading || isImporting,
          },
        ]}
      >
        <Stack gap={3}>
          <InfoCard
            icon={UploadFileOutlined}
            title={t('trainings.generate.import.title')}
            text={t('trainings.generate.import.description')}
            variant="info"
            direction="row"
            action={{
              label: t('common.change'),
              onClick: () => {
                closeModal();
                openModal({ component: GenerateTrainingModal, size: 'xl' });
              },
            }}
          />

          <Controller
            name="name"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <TextInput
                label={t('alternatives.systemName')}
                name={name}
                value={value}
                onChange={onChange}
                required
                ref={ref}
              />
            )}
          />

          <Stack gap={1}>
            <Controller
              name="fileIds"
              control={control}
              render={({ field: { name, onChange } }) => (
                <FileInput
                  name={name}
                  maxFiles={1}
                  accept={['pdf', 'doc', 'docx', 'ppt', 'pptx']}
                  onChange={onChange}
                  variant="compact"
                  required
                />
              )}
            />

            <Typography variant="p1" color="textSecondary">
              {t('trainings.generate.fileIds.info', {
                count: MAX_QUESTIONS_FOR_AI_GENERATION,
              })}
            </Typography>
          </Stack>
        </Stack>
      </Modal.Content>
    </form>
  );
};
